import PropTypes from 'prop-types'
import { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'

import Button from '@/components/Button'
import { Plus } from '@/components/Icons'
import Page from '@/components/Page'
import { resetRequest } from '@/store/actions'
import { isRequestError, isRequestPending } from '@/store/selectors'

import {
  clearCurrentToolbar,
  createToolbarInstance,
  deleteToolbarInstance,
  fetchToolbarInstances,
  setCurrentToolbar,
  updateToolbarInstance
} from './actions'
import {
  ADD_TOOLBAR_INSTANCE,
  CLEAR_CURRENT_TOOLBAR,
  DELETE_TOOLBAR_INSTANCE,
  FETCH_TOOLBAR_INSTANCES,
  SET_CURRENT_TOOLBAR,
  UPDATE_TOOLBAR_INSTANCE
} from './actionTypes'
import Modal from './AddEditModal'
import messages from './messages'
import { getCurrentToolbar, getToolbars } from './selectors'
import ToolbarInstances from './ToolbarInstances'
import ToolbarLanding from './ToolbarLanding'

const errorsToHandle = [
  { type: FETCH_TOOLBAR_INSTANCES, data: { detailCode: 'FTBFAL' } },
  { type: DELETE_TOOLBAR_INSTANCE, data: { detailCode: 'DTBFAL' } },
  { type: ADD_TOOLBAR_INSTANCE, data: { detailCode: 'ATBFAL' } },
  { type: UPDATE_TOOLBAR_INSTANCE, data: { detailCode: 'UTBFAL' } }
]

class OrganizationToolbarsPage extends Component<any, any> {
  constructor(props: any) {
    super(props)
    this.state = {
      showAddModal: false,
      showEditModal: false,
      showDeleteModal: false,
      currentToolbar: {}
    }
  }
  static displayName = 'OrganizationToolbarsPage'

  static propTypes = {
    actions: PropTypes.shape({
      fetchToolbarInstances: PropTypes.func.isRequired,
      createToolbarInstance: PropTypes.func.isRequired,
      resetRequest: PropTypes.func.isRequired,
      deleteToolbarInstance: PropTypes.func.isRequired,
      updateToolbarInstance: PropTypes.func.isRequired,
      setCurrentToolbar: PropTypes.func.isRequired,
      clearCurrentToolbar: PropTypes.func.isRequired
    }).isRequired,
    isDeleting: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    isAdding: PropTypes.bool.isRequired
  }

  static defaultProps = {
    isLoading: false,
    toolbars: [],
    currentToolbar: {}
  }

  componentWillUnmount() {
    this.props.actions.resetRequest([
      FETCH_TOOLBAR_INSTANCES,
      SET_CURRENT_TOOLBAR,
      CLEAR_CURRENT_TOOLBAR,
      ADD_TOOLBAR_INSTANCE
    ])
  }

  componentDidMount() {
    this.props.actions.fetchToolbarInstances()
  }

  navigateToAddToolbar = () => {
    this.setState({ showAddModal: true })
  }

  onAddCancel = () => {
    this.setState({ showAddModal: false })
  }

  onAddClose = () => {
    this.props.actions.fetchToolbarInstances()
    this.setState({ showAddModal: false })
  }

  onAddSubmit = (toolbar: any) => {
    this.props.actions.createToolbarInstance(toolbar)
  }

  navigateToRemoveToolbar = (toolbarId: any) => {
    this.props.actions.deleteToolbarInstance(toolbarId)
  }

  navigateToSelectedToolbar = (toolbar: any) => {
    this.props.actions.updateToolbarInstance(toolbar)
  }

  selectedToolbar = (toolbar: any) => {
    this.props.actions.setCurrentToolbar(toolbar)
  }

  clearSelectedToolbar = () => {
    this.props.actions.clearCurrentToolbar()
  }

  getActionMessage = () => {
    if (this.props.isDeleting) {
      return <FormattedMessage {...messages.deleting} />
    } else if (this.props.isUpdating) {
      return <FormattedMessage {...messages.updating} />
    } else if (this.props.isAdding) {
      return <FormattedMessage {...messages.adding} />
    } else {
      return <FormattedMessage {...messages.loading} />
    }
  }

  render() {
    const { showAddModal } = this.state
    const { currentToolbar, toolbars } = this.props

    return (
      <Page
        className="organization-toolbar-wrapper"
        errorsToHandle={errorsToHandle}
        isLoading={this.props.isLoading || this.props.isDeleting || this.props.isUpdating || this.props.isAdding}
        loadingMessage={this.getActionMessage()}
      >
        {
          <>
            {toolbars.length > 0 ? (
              <>
                <Page.Header
                  title={<FormattedMessage {...messages.title} />}
                  buttons={
                    <Button color="primary" onClick={this.navigateToAddToolbar}>
                      <Plus />
                      <FormattedMessage {...messages.createToolbar} />
                    </Button>
                  }
                />
                <ToolbarInstances
                  toolbars={toolbars}
                  currentToolbar={currentToolbar}
                  onRemoveToolbar={this.navigateToRemoveToolbar}
                  onEditToolbar={this.navigateToSelectedToolbar}
                  onSelectToolbar={this.selectedToolbar}
                  onClearSelectedToolbar={this.clearSelectedToolbar}
                />
              </>
            ) : (
              <ToolbarLanding addToolbar={this.navigateToAddToolbar} />
            )}

            {/* Toggling the modal open property does not reset the form state, conditionally rendering ensures it is reset each time it's opened */}
            {showAddModal && (
              <Modal
                open={showAddModal}
                onClose={currentToolbar.clientCredential ? this.onAddClose : this.onAddCancel}
                onSubmit={this.onAddSubmit}
                formValues={currentToolbar}
              />
            )}
          </>
        }
      </Page>
    )
  }
}

export { /** @deprecated */ OrganizationToolbarsPage }

function mapStateToProps(state: any) {
  return {
    hasError: isRequestError(state, [
      FETCH_TOOLBAR_INSTANCES,
      DELETE_TOOLBAR_INSTANCE,
      ADD_TOOLBAR_INSTANCE,
      UPDATE_TOOLBAR_INSTANCE
    ]),
    isLoading: isRequestPending(state, [FETCH_TOOLBAR_INSTANCES]),
    isDeleting: isRequestPending(state, DELETE_TOOLBAR_INSTANCE),
    isUpdating: isRequestPending(state, UPDATE_TOOLBAR_INSTANCE),
    isAdding: isRequestPending(state, ADD_TOOLBAR_INSTANCE),
    toolbars: getToolbars(state),
    currentToolbar: getCurrentToolbar(state)
  }
}

function mapDispatchToProps(dispatch: any) {
  return {
    actions: bindActionCreators(
      {
        fetchToolbarInstances,
        resetRequest,
        deleteToolbarInstance,
        createToolbarInstance,
        updateToolbarInstance,
        setCurrentToolbar,
        clearCurrentToolbar
      },
      dispatch
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(OrganizationToolbarsPage)
